import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Modal,
  Box,
  TextField,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Edit, Delete, Add, Close } from '@mui/icons-material';
import axios from 'axios';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

const API_URL = process.env.REACT_APP_API_URL;

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [usuarioAtual, setUsuarioAtual] = useState({
    id: null,
    nome: '',
    usuario: '',
    senha: '',
    permissao: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, mensagem: '', tipo: 'success' });

  useEffect(() => {
    fetchUsuarios();
  }, []);

const columns = useMemo(() => [
  { accessorKey: 'id', header: 'ID', size: 80 },
  { accessorKey: 'nome', header: 'Nome', filterVariant: 'text' },
  { accessorKey: 'usuario', header: 'Usuário', filterVariant: 'text' },
  {
    accessorKey: 'permissao',
    header: 'Permissão',
    filterVariant: 'select',
    filterSelectOptions: ['admin', 'funcionario'],
    Cell: ({ cell }) => cell.getValue() === 'admin' ? 'Admin' : 'Funcionário'
  },
  {
    id: 'acoes',
    header: 'Ações',
    Cell: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton onClick={() => handleAbrirModal(row.original)}>
          <Edit sx={{ color: "#ed3237" }} />
        </IconButton>
        <IconButton color="error" onClick={() => handleExcluir(row.original.id)}>
          <Delete />
        </IconButton>
      </Box>
    )
  }
], []);

const table = useMaterialReactTable({
  columns,
  data: usuarios,
  localization: MRT_Localization_PT_BR,
  enableColumnFilters: true,
  enableGlobalFilter: true,
  enablePagination: true,
  enableSorting: true,
  initialState: {
    pagination: { pageSize: 50, pageIndex: 0 }
  },
  muiTablePaperProps: {
    elevation: 3,
    sx: { borderRadius: 2, mt: 3 }
  },
  muiTableContainerProps: {
    sx: { maxHeight: '65vh', overflowY: 'auto' }
  },
  muiTableHeadCellProps: {
    sx: { backgroundColor: "#f9f9f9", fontWeight: 'bold', color: '#333' }
  },
});



  const fetchUsuarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/usuarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsuarios(res.data);
    } catch {
      setSnackbar({ open: true, mensagem: 'Erro ao carregar usuários.', tipo: 'error' });
    }
  };

  const handleAbrirModal = (usuario = { id: null, nome: '', usuario: '', senha: '', permissao: '' }) => {
    setUsuarioAtual(usuario);
    setModalOpen(true);
  };

  const handleFecharModal = () => {
    setModalOpen(false);
    setUsuarioAtual({ id: null, nome: '', usuario: '', senha: '', permissao: '' });
  };

  const handleSalvar = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (usuarioAtual.id) {
        // Atualizar senha
        await axios.put(`${API_URL}/trocar-senha/${usuarioAtual.id}`, 
          { senha: usuarioAtual.senha },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Criar novo usuário
        await axios.post(`${API_URL}/cadastrar-usuario`, 
          {
            nome: usuarioAtual.nome,
            usuario: usuarioAtual.email,
            senha: usuarioAtual.senha,
            permissao: usuarioAtual.tipo
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      fetchUsuarios();
      handleFecharModal();
      setSnackbar({ open: true, mensagem: 'Usuário salvo com sucesso!', tipo: 'success' });
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setSnackbar({ open: true, mensagem: error.response?.data?.mensagem || 'Erro ao salvar usuário.', tipo: 'error' });
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja excluir este usuário?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/excluir-usuario/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsuarios();
      setSnackbar({ open: true, mensagem: 'Usuário excluído com sucesso!', tipo: 'success' });
    } catch {
      setSnackbar({ open: true, mensagem: 'Erro ao excluir usuário.', tipo: 'error' });
    }
  };

  return (
    <Container maxWidth="" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h6" sx={{ mt: 5, mb: 3 }}>
        Usuários
      </Typography>

      <Button
        variant="contained"
        startIcon={<Add sx={{ color: '#ed3237' }} />}
        onClick={() => handleAbrirModal()}
        sx={{
          backgroundColor: "#3e4095",
          fontWeight: "bold",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#333",
            boxShadow: "0 0 0 2px #ed3237 inset"
          }
        }}
      >
        Adicionar
      </Button>

     <MaterialReactTable table={table} />


      {/* Modal */}
      <Modal open={modalOpen} onClose={handleFecharModal}>
        <Box
          sx={{
            width: 520,
            bgcolor: "#3e4095",
            color: "#fff",
            p: 4,
            mx: "auto",
            mt: "8%",
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            position: "relative"
          }}
        >
          <IconButton
            onClick={handleFecharModal}
            sx={{ position: "absolute", top: 10, right: 10, color: "#ed3237" }}
          >
            <Close />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#ed3237" }}>
            {usuarioAtual.id ? "Editar usuário" : "Adicionar usuário"}
          </Typography>

          <TextField
            fullWidth
            label="NOME"
            name="nome"
            value={usuarioAtual.nome}
            onChange={(e) => setUsuarioAtual({ ...usuarioAtual, nome: e.target.value })}
            sx={{
              mt: 2,
              "& .MuiInputBase-input": { color: "#fff" },
              "& .MuiInputLabel-root": { color: "#ccc" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#666" },
                "&:hover fieldset": { borderColor: "#ed3237" },
                "&.Mui-focused fieldset": { borderColor: "#ed3237" }
              }
            }}
          />

          <TextField
            fullWidth
            label="USUÁRIO"
            name="usuario"
            value={usuarioAtual.usuario}
            onChange={(e) => setUsuarioAtual({ ...usuarioAtual, usuario: e.target.value })}
            sx={{
              mt: 2,
              "& .MuiInputBase-input": { color: "#fff" },
              "& .MuiInputLabel-root": { color: "#ccc" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#666" },
                "&:hover fieldset": { borderColor: "#ed3237" },
                "&.Mui-focused fieldset": { borderColor: "#ed3237" }
              }
            }}
          />

          <TextField
            fullWidth
            label="SENHA"
            name="senha"
            type="password"
            value={usuarioAtual.senha}
            onChange={(e) => setUsuarioAtual({ ...usuarioAtual, senha: e.target.value })}
            sx={{
              mt: 2,
              "& .MuiInputBase-input": { color: "#fff" },
              "& .MuiInputLabel-root": { color: "#ccc" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#666" },
                "&:hover fieldset": { borderColor: "#ed3237" },
                "&.Mui-focused fieldset": { borderColor: "#ed3237" }
              }
            }}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: "#ccc" }}>Permissão</InputLabel>
            <Select
              value={usuarioAtual.permissao}
              onChange={(e) => setUsuarioAtual({ ...usuarioAtual, permissao: e.target.value })}
              sx={{
                color: "#fff",
                ".MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#ed3237" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ed3237" }
              }}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="funcionario">Funcionário</MenuItem>
            </Select>
          </FormControl>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSalvar}
            sx={{
              mt: 4,
              backgroundColor: "#ed3237",
              color: "#3e4095",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#ffa733",
                color: "#000"
              }
            }}
          >
            Salvar
          </Button>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.tipo} sx={{ width: "100%" }}>
          {snackbar.mensagem}
        </Alert>
      </Snackbar>
    </Container>
  );
}
